import React, { FC, ReactNode, useCallback } from 'react';
import classNames from 'classnames';
import styled from 'styled-components';
import GroupedItemActionButton from '../../shop/cart/GroupedItemActionButton';
import { ChangeQuantityDialog } from '../common/ChangeQuantityDialog';
import SelectionlistActionButtons from '../../shop/selectionlists/SelectionlistActionButtons';
import RemoveFromProductlistButton from '../../shop/selectionlists/RemoveFromProductlistButton';
import ProductList from '../common/ProductList';
import Pagination from '../common/Pagination';
import BundleCover from './BundleCover';
import BundleBadge from './BundleBadge';
import { GroupedByItemProps } from '../common/GroupedByList';
import { getGroupItemId, isBundleGroupItem } from '../../../models/GroupItem';
import { Orderlist } from '../../../models/OrderList';
import { LoanFormat, mapLoanFormat } from '../../../models/LoanFormat';
import { formatLoanFormat } from '../utils/formatLoanFormat';
import { Trans, useTranslation } from 'react-i18next';
import { BulkAddProductsToSelectionListResponse } from '../../store/api';
import { useListContentChecks } from '../common/useListContentChecks';
import { CopyOrMoveOptions } from '../../../models/ListContentCheckOptions';
import { ListActionTarget } from '../../../models/ListActionTarget';
import { ACTION_COPY, ACTION_MOVE } from '../../store/constants';
import { CheckBeforeAddingModal } from '../common/CheckBeforeAddingModal';
import { UnavailableProductsModal } from '../common/UnavailableProductsModal';

const StChangeQuantityDialog = styled(ChangeQuantityDialog)`
    margin-right: 35px;
`;

const GroupedByItem: FC<GroupedByItemProps> = props => {
    const { t } = useTranslation();
    const {
        item,
        grouping,
        isGroupAddAction = false,
        totalProducts,
        currentPage = 0,
        pageSize = 20,
        onPageChange,
        onToggleList,
        onRemoveGroupedItemFromList,
        onGroupAmountChange,
        onCopyGroupToList,
        onMoveGroupToList,
        onAddGroupedItemToCart
    } = props;

    const itemId = getGroupItemId(item);
    const isIndividualGroup = itemId.indexOf('individual') > -1;
    const isExpanded = item.bundleId === grouping.id;

    const onAction = useCallback(
        (performChecks: boolean, options: CopyOrMoveOptions): Promise<BulkAddProductsToSelectionListResponse> => {
            if (options.target === ListActionTarget.Cart) {
                throw new Error('technical error');
            }

            return options.action === ACTION_MOVE
                ? onMoveGroupToList(item, options.list, performChecks)
                : onCopyGroupToList(item, options.list, performChecks);
        },
        [item, onCopyGroupToList, onMoveGroupToList]
    );

    const { changePrice, getCheckBeforeAddingModalProps, getUnavailableProductsModalProps } =
        useListContentChecks<CopyOrMoveOptions>({ onAction });

    const handleCopyToList = useCallback(
        (list: Orderlist) => changePrice({ target: ListActionTarget.List, list, action: ACTION_COPY }),
        [changePrice]
    );

    const handleMoveToList = useCallback(
        (list: Orderlist) => changePrice({ target: ListActionTarget.List, list, action: ACTION_MOVE }),
        [changePrice]
    );

    if (!isBundleGroupItem(item)) {
        throw new Error('<GroupedByItem> can not render grouped by list items');
    }

    const amountInfo: ReactNode[] = [];
    // TODO [i18n] css adjust margin between loanformats (if ePress products are listed)
    if (isIndividualGroup || !item.loanFormat) {
        if (item.eaudiobookProductAmount) {
            const loanFormatLabel = formatLoanFormat(
                LoanFormat.eAudiobooks,
                item.eaudiobookProductAmount > 1 ? 'plural' : 'singular'
            );
            amountInfo.push(
                <div style={{ display: 'inline-block', marginRight: '30px' }} key="eaudiobook">
                    <Trans
                        i18nKey="selectionList.groupedByItem.alreadyInYourLibrary"
                        components={[<div className="txt-normal strong" />, <div />]}
                        values={{
                            productAmount: item.eaudiobookProductAmount,
                            loanFormat: loanFormatLabel,
                            stockAmount: item.eaudiobookProductStockAmount
                        }}
                    />
                </div>
            );
        }
        if (item.ebookProductAmount) {
            const loanFormatLabel = formatLoanFormat(
                LoanFormat.eBooks,
                item.ebookProductAmount > 1 ? 'plural' : 'singular'
            );
            amountInfo.push(
                <div style={{ display: 'inline-block' }} key="ebook">
                    <Trans
                        i18nKey="selectionList.groupedByItem.alreadyInYourLibrary"
                        components={[<div className="txt-normal strong" />, <div />]}
                        values={{
                            productAmount: item.ebookProductAmount,
                            loanFormat: loanFormatLabel,
                            stockAmount: item.ebookProductStockAmount
                        }}
                    />
                </div>
            );
        }

        if (item.epressProductAmount) {
            const loanFormatLabel = formatLoanFormat(
                LoanFormat.ePress,
                item.epressProductAmount > 1 ? 'plural' : 'singular',
                true
            );
            amountInfo.push(
                <div style={{ display: 'inline-block' }} key="epress">
                    <Trans
                        i18nKey="selectionList.groupedByItem.alreadyInYourLibraryWithTitle"
                        components={[<div className="txt-normal strong" />, <div />]}
                        values={{
                            productAmount: item.epressProductAmount,
                            loanFormat: loanFormatLabel,
                            stockAmount: item.epressProductStockAmount ?? 0
                        }}
                    />
                </div>
            );
        }
    } else {
        const amount = item.eaudiobookProductAmount || item.ebookProductAmount || item.epressProductAmount;
        const total = item.catalogueBundleSize ?? 0;
        const loanFormatLabel = formatLoanFormat(
            mapLoanFormat(item.loanFormat)!,
            total > 1 ? 'plural' : 'singular',
            true
        );
        const stock =
            item.eaudiobookProductStockAmount || item.ebookProductStockAmount || item.epressProductStockAmount || 0;

        amountInfo.push(
            <div key="amount">
                <Trans
                    i18nKey="selectionList.groupedByItem.selected"
                    components={[<div className="txt-normal strong" />, <div />]}
                    values={{
                        amount: amount,
                        total: total,
                        loanFormat: loanFormatLabel,
                        stockAmount: stock
                    }}
                />
            </div>
        );
    }

    const classNamesExpand = classNames('material-icons', 'md-normal', 'expand-arrow', { hidden: isExpanded });
    const classNamesCollapse = classNames('material-icons', 'md-normal', 'expand-arrow', { hidden: !isExpanded });

    let removeButton: ReactNode = null;
    if (isGroupAddAction) {
        removeButton = (
            <RemoveFromProductlistButton product={item} onRemoveProductFromList={onRemoveGroupedItemFromList} />
        );
    }

    let changeQuantityDialog: ReactNode = null;
    if (!isIndividualGroup) {
        changeQuantityDialog = (
            <StChangeQuantityDialog
                label={t('selectionList.groupedByItem.quantity')}
                className="fl-left"
                onChange={(command, amount) => onGroupAmountChange?.(item, amount, command)}
            />
        );
    }

    return (
        <div className="item">
            <div className="tile">
                <div className="cover">
                    <BundleCover bundle={item} />
                </div>
                <div className="meta">
                    {item.salesTag && <BundleBadge bundle={item} />}
                    <div className="txt-normal strong">
                        {t(`selectionList.groupedByItem.${item.bundleId}.title`, {
                            defaultValue: item.title
                        })}
                    </div>
                    <div className="description">
                        {t(`selectionList.groupedByItem.${item.bundleId}.description`, {
                            defaultValue: item.description
                        })}
                    </div>
                    <div className="fadeout" />
                    {amountInfo}
                </div>

                <div className="action">
                    <div className="buttons">
                        {changeQuantityDialog}
                        {removeButton}
                        <SelectionlistActionButtons
                            onCopyProductToList={handleCopyToList}
                            onMoveProductToList={handleMoveToList}
                            hideListId={props.currentListId}
                        />
                        {item.ebookCopyAmount ? (
                            <span className="marker-badge txt-smallest fl-right">
                                <i className="bolinda-icons">book</i>
                                <span>{item.ebookCopyAmount}</span>
                            </span>
                        ) : null}
                        {item.eaudiobookCopyAmount ? (
                            <span className="marker-badge txt-smallest fl-right">
                                <i className="bolinda-icons">audiobook</i>
                                <span>{item.eaudiobookCopyAmount}</span>
                            </span>
                        ) : null}
                        {item.epressCopyAmount ? (
                            <span className="marker-badge txt-smallest fl-right gild">
                                <i className="icon emagazine-filled" />
                                <span>{item.epressCopyAmount}</span>
                            </span>
                        ) : null}
                        <GroupedItemActionButton
                            item={item}
                            onRemove={onRemoveGroupedItemFromList}
                            onAddToCart={onAddGroupedItemToCart}
                            isAddAction={isGroupAddAction}
                        />
                    </div>
                    <div className="a-right">
                        <a onClick={() => onToggleList?.(item.bundleId)} className="expand no-user-select">
                            {t('selectionList.groupedByItem.showTitles')}
                            <i className={classNamesExpand}>keyboard_arrow_down</i>
                            <i className={classNamesCollapse}>keyboard_arrow_up</i>
                        </a>
                    </div>
                </div>
            </div>
            {isExpanded ? (
                <div className="item-products">
                    <Pagination
                        totalItems={totalProducts}
                        onPageChange={onPageChange}
                        currentPage={currentPage}
                        pageSize={pageSize}
                    />
                    <ProductList
                        {...props}
                        loanFormat={item.loanFormat}
                        tileItemComponent={props.tileItemComponent}
                        listItemComponent={props.listItemComponent}
                        currentPage={currentPage}
                    />
                    <Pagination
                        totalItems={totalProducts}
                        onPageChange={onPageChange}
                        currentPage={currentPage}
                        pageSize={pageSize}
                        placeholder={true}
                    />
                </div>
            ) : null}
            <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
            <UnavailableProductsModal {...getUnavailableProductsModalProps()} />
        </div>
    );
};

export default GroupedByItem;
